// Copyright (c) StrangeBee 2024

import { useLocalMessage } from '@Helpers/';
import useValidateForm from '@Hooks/useValidateForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import React, { ReactElement } from 'react';
import { ObservableOutput } from 'thehive-sdk';
import { ObservableEventList } from '../eventList';
import { ObservableBulkEditForm, ObservableBulkEditValues } from './ObservableBulkEditForm';

interface ObservableBulkEditManageProps {
    triggerElem: ReactElement;
    observables: ObservableOutput[];
    onBulkEdit: ObservableEventList['onBulkEdit'];
}

export const ObservableBulkEditManage = ({
    triggerElem,
    observables,
    onBulkEdit,
}: ObservableBulkEditManageProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinish = (values: ObservableBulkEditValues): void => {
        const valuesProcessed: any = Object.fromEntries(
            Object.entries(values).filter(([key]) => {
                return !key.includes('Checked') && values[`${key}Checked`];
            }),
        );

        onBulkEdit(observables, valuesProcessed);

        form.resetFields();
    };

    return (
        <THDrawer
            title={i('observable.bulk-edit.drawer.title')}
            triggerElem={triggerElem}
            afterVisibleChange={() => form.resetFields()}
            className="drawer-form"
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <ObservableBulkEditForm
                    onCancel={() => {
                        form.resetFields();
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                    form={form}
                    isValid={isValid && fieldsTouched}
                    onFinish={(values) => {
                        onFinish(values);
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                />
            )}
        </THDrawer>
    );
};

export default ObservableBulkEditManage;
